import { css } from '@/styled-system/css';

// Fixed widths per column, applied to the header cells to drive the `tableLayout: fixed` columns.
export const tableCellContentWidths = {
  UNIT: css.raw({
    width: {
      // NOTE: use `100%` here to display row borders fully across the table
      base: '100%',
      lg: '300px',
    },
  }),
  RENT: css.raw({
    width: {
      base: 'auto',
      lg: '142px',
    },
  }),
  STATUS: css.raw({
    width: {
      base: 'auto',
      lg: '120px',
    },
  }),
  TENANTS: css.raw({
    width: {
      base: 'auto',
      lg: '150px',
    },
  }),
  OVERFLOW: css.raw({ width: '80px' }),
} as const;

// Fixed widths per column for the top-level "Properties" table view.
export const propertyTableCellContentWidths = {
  ADDRESS: css.raw({
    width: {
      // NOTE: use `100%` here to display row borders fully across the table
      base: '100%',
      lg: '400px',
    },
  }),
  TYPE: css.raw({
    width: {
      base: 'auto',
      lg: '240px',
    },
  }),
  UNITS: css.raw({
    width: {
      base: 'auto',
      lg: '240px',
    },
  }),
  OVERFLOW: css.raw({ width: '80px' }),
} as const;

export const cardStyles = css.raw({
  padding: 'none',
  overflow: 'hidden',
  borderColor: 'border.muted',
});

export const headerStyles = css.raw({
  display: 'flex',
  alignItems: 'center',
  gap: '400',
  padding: '400',
});

export const overflowMenuStyles = css.raw({
  flexShrink: 0,
});

export const photoContainerStyles = css.raw({
  display: { base: 'none', md: 'block' },
  width: '120px',
  height: '80px',
  flexShrink: 0,
  borderRadius: 'obj.default',
  overflow: 'hidden',
});

export const infoBlockStyles = css.raw({
  display: 'flex',
  flexDirection: 'column',
  gap: '100',
  flex: 1,
  minWidth: 0,
});

export const unitsTableContainerStyles = css.raw({
  borderTopWidth: 'default',
  borderTopStyle: 'solid',
  borderTopColor: 'border.muted',
  // The interactive Card sets `cursor: pointer`, which inherits down to the whole panel even
  // though clicks here are ignored; reset it so only truly interactive children show a pointer.
  cursor: 'auto',
});

export const unitCellStyles = css.raw({
  paddingLeft: '1400',
});

export const unitCellContentStyles = css.raw({
  display: 'flex',
  alignItems: 'center',
  gap: '300',
});

export const unitThumbnailStyles = css.raw({
  // Hide the thumbnail on small screens so the unit details get the full width.
  display: { base: 'none', md: 'block' },
  width: '120px',
  height: '64px',
  flexShrink: 0,
  borderRadius: 'obj.default',
  overflow: 'hidden',
});

export const unitInfoStyles = css.raw({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

// Address column thumbnail for the top-level Properties table; hidden on small screens
// so the address details get the full width.
export const propertyThumbnailStyles = css.raw({
  display: { base: 'none', md: 'block' },
  width: '120px',
  height: '80px',
  flexShrink: 0,
  borderRadius: 'obj.default',
  overflow: 'hidden',
});

// Overflow menu column keeps the trigger aligned to the right edge of the row.
export const overflowMenuCellStyle = css.raw({
  whiteSpace: 'nowrap',
  textAlign: 'right',
});

// Makes the row-wrapping Anchor behave as a normal table row (no layout box of its own)
// while keeping real anchor semantics: hovering shows the destination URL and cmd/ctrl-click
// opens it in a new tab.
export const propertyRowLinkStyles = css.raw({
  display: 'contents',
  color: 'inherit',
  textDecoration: 'none',
});

// Remove the bottom border on the final row
export const lastRowStyles = css.raw({
  '& td': {
    borderBlockEndWidth: '0',
  },
});

export const tenantsListStyles = css.raw({
  display: 'flex',
  alignItems: 'center',
  gap: '200',
  minWidth: 0,
});

// "+N" chip that triggers the additional-tenants popover.
export const tenantsOverflowChipStyles = css.raw({
  // Unset the minWidth to allow the chip to shrink to fit the content.
  minWidth: 'unset',
  backgroundColor: 'bg.impact',
});

export const tenantsOverflowChipLabelStyles = css.raw({
  color: 'text.onImpact.neutral-fixed',
});

// Single-line text that ellipsizes when it overflows; pair with TruncatedTextWithTooltip.
export const truncatedTextStyles = css.raw({
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Secondary columns collapse below `lg` so the table stays readable on narrow screens.
// Apply to both Table.Cell and Table.HeaderCell via the `css` prop to keep columns aligned.
export const hiddenMobileCellStyles = css.raw({
  display: { base: 'none', lg: 'table-cell' },
});
